import { QueryClient } from '@tanstack/react-query';
import type { StreamEvent } from '@pacer/shared';

export const queryKeys = {
  me: ['me'] as const,
  household: ['household'] as const,
  members: ['members'] as const,
  statuses: ['statuses'] as const,
  locations: ['locations'] as const,
  scores: ['scores'] as const,
  activity: ['activity'] as const,
  info: ['info'] as const,
  vaultStatus: ['vaultStatus'] as const,
  categories: ['categories'] as const,
  events: (from: string, to: string, memberId?: string | null) =>
    ['events', from, to, memberId ?? null] as const,
  lists: ['lists'] as const,
  meals: ['meals'] as const,
  messages: ['messages'] as const,
  countdowns: ['countdowns'] as const,
  chores: ['chores'] as const,
  feeds: ['feeds'] as const,
  photos: ['photos'] as const,
  weather: ['weather'] as const,
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // SSE is the freshness mechanism, so background refetching is mostly redundant —
      // but a wall tablet can sleep and miss pushes, so refetch when it wakes.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, err) => {
        // A 401 means "log in", not "try again".
        if (err instanceof Error && 'status' in err && (err as { status: number }).status === 401) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

/** Maps a server push onto the query caches it invalidates. */
export function invalidateFor(event: StreamEvent): void {
  switch (event) {
    case 'events:changed':
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      break;
    case 'members:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.members });
      // Member colours and avatars are denormalized into rendered events.
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      break;
    case 'status:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.statuses });
      break;
    case 'locations:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.locations });
      break;
    case 'stats:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.scores });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activity });
      break;
    case 'info:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.info });
      break;
    case 'vault:changed':
      // Only the configured/locked flag is cached; unlocked contents live in component
      // state and are re-fetched on the next unlock, never invalidated from here.
      void queryClient.invalidateQueries({ queryKey: queryKeys.vaultStatus });
      break;
    case 'comments:changed':
      void queryClient.invalidateQueries({ queryKey: ['event-comments'] });
      break;
    case 'categories:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.categories });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      break;
    case 'lists:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.lists });
      break;
    case 'meals:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.meals });
      break;
    case 'messages:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages });
      break;
    case 'countdowns:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.countdowns });
      break;
    case 'chores:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.chores });
      break;
    case 'feeds:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.feeds });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      break;
    case 'photos:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.photos });
      break;
    case 'household:changed':
      void queryClient.invalidateQueries({ queryKey: queryKeys.household });
      break;
    case 'ping':
      break;
  }
}
